// Посты X в статьях: карточка Ghost показывает цитату — текст, автора и дату со ссылкой
// на пост, — а скрипт X грузится только по нажатию «Show post on X». Так 591 KiB скрипта
// и cookies X не тормозят статью (п.1 ТЗ «agents SEO», 22.10.2026); без JS цитата
// остаётся читаемой. Скрипт X берётся один раз за сессию.
//
// Скрипт X при загрузке сам ищет в документе цитаты с классом twitter-tweet и рисует их
// все. Поэтому закрытая цитата этот класс теряет (QUOTE_CLASS вместо него), а пост рисуется
// явно — twttr.widgets.createTweet по ID поста — только в той карточке, которую открыли.
// Вызывается на каждой странице (pageInit) и после PJAX.

const GHOST_QUOTES = '.gh-content .kg-embed-card > blockquote.twitter-tweet';
const QUOTE_CLASS = 'th-tweet-quote';
const BUTTON_CLASS = 'th-tweet-show';
// В статье, которую ещё не очистили от скрипта X, он сам рисует твит — её не трогаем.
const OWN_LOADER = 'script[src*="/widgets.js"]';
// Ссылка-дата внизу цитаты — permalink самого поста (ссылки в тексте могут вести на другие).
const PERMALINK = /^https:\/\/(?:x|twitter)\.com\/[^/]+\/status\/(\d+)/;
const WIDGETS_URL = 'https://platform.x.com/widgets.js';
const LOAD_TIMEOUT_MS = 10000;
const RENDER_TIMEOUT_MS = 10000;

let widgetsPromise = null;

export default function tweetEmbeds() {
    document.querySelectorAll(GHOST_QUOTES).forEach(closeQuote);
}

function closeQuote(quote) {
    const card = quote.parentElement;
    const permalink = [...quote.querySelectorAll('a[href]')].pop();
    const match = permalink?.href.match(PERMALINK);

    if (card.querySelector(OWN_LOADER)) {
        renderLegacyCard(card);
        return;
    }
    if (!match) {
        return;
    }
    quote.classList.replace('twitter-tweet', QUOTE_CLASS);
    const button = document.createElement('button');

    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.textContent = 'Show post on X';
    button.addEventListener('click', () => showPost(quote, button, match), { once: true });
    quote.after(button);
}

// Статья со своим тегом скрипта X (до очистки): при первой загрузке X рисует её сам, а после
// PJAX уже загруженный X документ заново не сканирует — карточку надо отдать ему явно.
function renderLegacyCard(card) {
    window.twttr?.widgets?.load?.(card);
}

async function showPost(quote, button, [permalink, postId]) {
    button.disabled = true;
    button.textContent = 'Loading post…';
    const slot = document.createElement('div');

    quote.after(slot);
    try {
        const twttr = await loadWidgets();
        const widget = await withTimeout(
            twttr.widgets.createTweet(postId, slot, { dnt: true }),
            RENDER_TIMEOUT_MS,
            'X did not render the post',
        );

        if (!widget) {
            throw new Error(`X returned no widget for post ${postId}`);
        }
        quote.hidden = true;
        button.remove();
    } catch (error) {
        console.error('[tweetEmbeds]', error);
        slot.remove();
        replaceWithLink(button, permalink);
    }
}

/**
 * Скрипт X — один на сессию. Уже загруженный (например, статьёй до очистки) берётся как
 * есть. Каждая попытка завершается один раз и сама снимает свои обработчики; провал
 * сбрасывает только свою попытку, и следующая карточка пробует заново.
 */
function loadWidgets() {
    if (window.twttr?.widgets?.createTweet) {
        return Promise.resolve(window.twttr);
    }
    if (!widgetsPromise) {
        const attempt = requestWidgets();

        widgetsPromise = attempt;
        attempt.catch(() => {
            if (widgetsPromise === attempt) {
                widgetsPromise = null;
            }
        });
    }
    return widgetsPromise;
}

function requestWidgets() {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        let settled = false;
        const finish = (error, twttr) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            script.removeEventListener('load', onLoad);
            script.removeEventListener('error', onError);
            if (error) {
                script.remove();
                reject(error);
            } else {
                resolve(twttr);
            }
        };
        const onLoad = () => {
            if (!window.twttr?.ready) {
                finish(new Error(`${WIDGETS_URL} loaded without window.twttr`));
                return;
            }
            window.twttr.ready((twttr) => finish(null, twttr));
        };
        const onError = () => finish(new Error(`${WIDGETS_URL} failed to load`));
        const timer = setTimeout(() => {
            finish(new Error(`${WIDGETS_URL}: no answer in ${LOAD_TIMEOUT_MS} ms`));
        }, LOAD_TIMEOUT_MS);

        script.src = WIDGETS_URL;
        script.async = true;
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        document.head.append(script);
    });
}

function withTimeout(promise, ms, message) {
    let timer = null;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${message} in ${ms} ms`)), ms);
    });

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Пост остаётся доступен: вместо кнопки — ссылка на него на X (она же стоит в дате цитаты).
function replaceWithLink(button, permalink) {
    const link = document.createElement('a');

    link.className = BUTTON_CLASS;
    link.href = permalink;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Couldn’t load the post — open it on X';
    button.replaceWith(link);
}
